/**
 * Minimal observable store. Use for transient app state (current user, current
 * conversation, modal stack). Persistent data lives in localState or Firebase.
 *
 * @template T
 */
export class Store {
  /** @param {T} initial */
  constructor(initial) {
    /** @type {T} */
    this._state = initial;
    /** @type {Set<(state: T, prev: T) => void>} */
    this._listeners = new Set();
  }

  /** @returns {T} */
  get() {
    return this._state;
  }

  /**
   * Replace state. Accepts either a partial object (merged) or an updater function.
   * Listeners are only fired if the reference changed.
   * @param {Partial<T> | ((prev: T) => T)} updater
   */
  set(updater) {
    const prev = this._state;
    const next =
      typeof updater === 'function'
        ? /** @type {(prev: T) => T} */ (updater)(prev)
        : { ...prev, ...updater };
    if (next === prev) return;
    this._state = next;
    for (const fn of this._listeners) {
      try {
        fn(next, prev);
      } catch (err) {
        console.error('[store] listener threw:', err);
      }
    }
  }

  /**
   * Subscribe to all state changes.
   * @param {(state: T, prev: T) => void} fn
   * @returns {() => void}
   */
  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /**
   * Subscribe to a single derived slice. Listener only fires when the slice changes.
   * @template S
   * @param {(state: T) => S} selector
   * @param {(slice: S, prev: S) => void} fn
   * @returns {() => void}
   */
  select(selector, fn) {
    let prevSlice = selector(this._state);
    return this.subscribe((state) => {
      const nextSlice = selector(state);
      if (nextSlice !== prevSlice) {
        const old = prevSlice;
        prevSlice = nextSlice;
        fn(nextSlice, old);
      }
    });
  }
}

/**
 * @typedef {Object} AppState
 * @property {string | null} uid
 * @property {string | null} plate
 * @property {string | null} phone
 * @property {boolean} authReady
 * @property {boolean} online
 * @property {boolean} adShowing
 * @property {Set<string>} openModals
 */

/** @type {Store<AppState>} */
export const appStore = new Store({
  uid: null,
  plate: null,
  phone: null,
  authReady: false,
  online: true,
  adShowing: false,
  openModals: new Set(),
});
